import { Illustration, Text } from "@clipboard-health/ui-components";
// eslint-disable-next-line no-restricted-imports -- TextField is needed for multiline input
import { Stack, TextField } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useState } from "react";

import {
  buildWorkerOutcomeSteps,
  type InterviewExperienceResult,
  useUpdateInterview,
  type WorkerOutcomeResult,
} from "../../api/useUpdateInterview";
import { type FeedbackStep } from "./feedbackStep";
import { useInterviewAttendanceSource } from "./useInterviewAttendanceSource";

interface FreeTextStepContentProps {
  interview: InterviewWithDetails;
  placementCandidate: PlacementCandidate;
  step: FeedbackStep;
  result: WorkerOutcomeResult;
  textFieldHeading: string;
  textFieldPlaceholder?: string;
  selectedInterviewExperience?: InterviewExperienceResult;
  refetchInterviews: () => Promise<unknown>;
  isFetchingInterviews: boolean;
  onSubmit: () => void;
}

export function FreeTextStepContent(props: FreeTextStepContentProps) {
  const {
    interview,
    placementCandidate,
    step,
    result,
    textFieldHeading,
    textFieldPlaceholder,
    selectedInterviewExperience,
    refetchInterviews,
    isFetchingInterviews,
    onSubmit,
  } = props;

  const source = useInterviewAttendanceSource();
  const [message, setMessage] = useState("");
  const { mutateAsync: updateInterview, isLoading: isUpdateInterviewLoading } =
    useUpdateInterview();

  const eventContext = {
    interview,
    placementCandidate,
    step,
    result,
    source,
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_OPTIONS_VIEWED, eventContext);

  async function handleSubmit() {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_OPTIONS_CLICKED, {
      ...eventContext,
      action: result,
      hasMessage: message.trim().length > 0,
    });
    const steps = buildWorkerOutcomeSteps(selectedInterviewExperience, result);
    await updateInterview({
      interviewId: interview.id,
      attributes: {
        workerOutcome: {
          result,
          ...(message.trim().length > 0 ? { message: message.trim() } : undefined),
          ...(steps ? { steps } : undefined),
        },
      },
    });
    await refetchInterviews();
    onSubmit();
  }

  return (
    <>
      <Stack
        spacing={7}
        alignItems="center"
        justifyContent="center"
        sx={{ paddingX: 8, paddingY: 6 }}
      >
        <Illustration type="facility" />
        <Stack spacing={6} textAlign="center" width="100%">
          <Text>{textFieldHeading}</Text>
          <TextField
            multiline
            fullWidth
            minRows={3}
            maxRows={5}
            value={message}
            placeholder={textFieldPlaceholder ?? "Tell us anything that might be helpful to know."}
            onChange={(event) => {
              setMessage(event.target.value);
            }}
          />
        </Stack>
      </Stack>
      <DialogFooter orientation="horizontal" sx={{ px: 8, pb: 8, pt: 2 }}>
        <Stack spacing={4} width="100%">
          <LoadingButton
            variant="contained"
            size="large"
            isLoading={isUpdateInterviewLoading}
            disabled={isUpdateInterviewLoading || isFetchingInterviews}
            onClick={async () => {
              await handleSubmit();
            }}
          >
            Submit
          </LoadingButton>
        </Stack>
      </DialogFooter>
    </>
  );
}
